/**
 * ConductScoreCircle - Shared visual indicator for Conduct Score (1-10 scale)
 *
 * Displays a solid colored circle with the conduct score.
 * Color is determined by score thresholds with smooth interpolation.
 *
 * Color thresholds (1-10 scale):
 * - 1-3: Low risk (green)
 * - 4-6: Medium risk (yellow/amber gradient)
 * - 7-8: High risk (orange gradient)
 * - 9-10: Critical risk (red)
 *
 * Used by: RecordDetail, RecordsTable, TodayPage
 */

import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';
import type { ConductScoreBreakdown } from '../../types/record';
import katex from 'katex';
import 'katex/dist/katex.min.css';

export interface ConductScoreCircleProps {
  score: number; // 1-10 scale
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showDecimal?: boolean; // Show decimal value (e.g., 7.2) instead of rounded
  breakdown?: ConductScoreBreakdown;
  isProcessing?: boolean; // Show subtle animated ring when record is being processed
  processingStage?: string | null; // Current processing stage for tooltip display
}

/**
 * Interpolate between two hex colors
 */
function interpolateColor(color1: string, color2: string, factor: number): string {
  const parseHex = (hex: string) => ({
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  });

  const c1 = parseHex(color1);
  const c2 = parseHex(color2);

  const r = Math.round(c1.r + (c2.r - c1.r) * factor);
  const g = Math.round(c1.g + (c2.g - c1.g) * factor);
  const b = Math.round(c1.b + (c2.b - c1.b) * factor);

  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Get the color for a conduct score on the 1-10 scale
 */
export function getConductScoreColor(score: number): string {
  // Clamp score between 1 and 10
  const clampedScore = Math.max(1, Math.min(10, score));

  // Color thresholds for 1-10 scale
  if (clampedScore <= 3) {
    return '#22C55E'; // Green - Low risk
  } else if (clampedScore <= 6) {
    // Interpolate between yellow-green and yellow-orange for medium
    const factor = (clampedScore - 3) / 3;
    return interpolateColor('#DAD154', '#FF9500', factor);
  } else if (clampedScore <= 8) {
    // Interpolate between orange-yellow and orange-red for high
    const factor = (clampedScore - 6) / 2;
    return interpolateColor('#FF9500', '#FF7950', factor);
  } else {
    // 9-10: Critical (red)
    return '#FF4343';
  }
}

const sizeConfig = {
  sm: {
    container: 'w-10 h-10',
    text: 'text-sm',
  },
  md: {
    container: 'w-14 h-14',
    text: 'text-lg',
  },
  lg: {
    container: 'w-20 h-20',
    text: 'text-2xl',
  },
  xl: {
    container: 'w-28 h-28',
    text: 'text-3xl',
  },
};

const DIMENSION_DISPLAY: Record<string, string> = {
  dissatisfaction: 'Dissatisfaction',
  foreseeable_harm: 'Foreseeable Harm',
  customer_strain: 'Customer Strain',
  vulnerability: 'Vulnerability',
  consumer_duty: 'Consumer Duty',
};

const DIMENSION_WEIGHTS: Record<string, number> = {
  dissatisfaction: 1.0,
  foreseeable_harm: 1.5,
  customer_strain: 1.0,
};

const BASE_DIMS = ['dissatisfaction', 'foreseeable_harm', 'customer_strain'] as const;

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, displayMode: false });
}

function Formula({ latex }: { latex: string }) {
  const html = useMemo(() => renderLatex(latex), [latex]);
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

const DIM_ABBREV: Record<string, string> = {
  dissatisfaction: 'D',
  foreseeable_harm: 'FH',
  customer_strain: 'CS',
};

function formatTopLabel(label?: string | null): string {
  if (!label) return '';
  return label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function BreakdownTooltipContent({ score, breakdown }: { score: number; breakdown: ConductScoreBreakdown }) {
  const dims = breakdown.dimensions;
  const calc = breakdown.calculation;

  if (!calc) {
    return (
      <div className="text-left py-1">
        <div className="font-medium">Conduct Score: {score.toFixed(1)} / 10</div>
      </div>
    );
  }

  const vRiskMod = calc.vulnerability_risk_mod ?? 0;
  const vUrgMod = calc.vulnerability_urgency_mod ?? 0;
  const hasVuln = vRiskMod > 0 || vUrgMod > 0;
  const normalizer = calc.normalizer ?? 36.25;

  // Build substituted max() inner strings
  const riskMaxInner = BASE_DIMS.map((d) => {
    const w = DIMENSION_WEIGHTS[d];
    const r = dims[d]?.risk ?? 0;
    return `${w} \\!\\times\\! ${r}`;
  }).join(',\\;');
  const urgMaxInner = BASE_DIMS.map((d) => {
    const w = DIMENSION_WEIGHTS[d];
    const u = dims[d]?.urgency ?? 0;
    return `${w} \\!\\times\\! ${u}`;
  }).join(',\\;');

  return (
    <div className="text-left space-y-2.5 py-1 min-w-[360px] max-w-[460px]">
      <div className="font-semibold text-sm">
        Conduct Score: {score.toFixed(2)} / 10
      </div>

      {/* Step 1: Per-dimension inputs — where values come from */}
      <div>
        <div className="text-[10px] font-medium opacity-60 uppercase tracking-wider mb-1">
          Step 1 — Per-dimension max risk &amp; urgency
        </div>
        <div className="text-[10px] opacity-40 mb-1.5">
          For each dimension, take the highest R and U from all detected labels.
        </div>
        <div className="space-y-1">
          {BASE_DIMS.map((dimId) => {
            const dim = dims[dimId];
            if (!dim) return null;
            const label = DIMENSION_DISPLAY[dimId] || dimId;
            const w = DIMENSION_WEIGHTS[dimId];
            const topLabel = formatTopLabel(dim.top_label);
            return (
              <div key={dimId}>
                <div className="flex justify-between gap-3 text-[11px]">
                  <span className="opacity-80">
                    {label}
                    {w !== 1.0 && <span className="opacity-50 ml-1">({w}x weight)</span>}
                  </span>
                  <span className="tabular-nums font-medium">
                    R={dim.risk ?? 0}  U={dim.urgency ?? 0}
                  </span>
                </div>
                {topLabel && (
                  <div className="text-[9px] opacity-40 ml-1">
                    Driven by: {topLabel}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-background/20" />

      {/* Step 2: Weighted aggregation */}
      <div>
        <div className="text-[10px] font-medium opacity-60 uppercase tracking-wider mb-1">
          Step 2 — Weighted aggregation
        </div>
        <div className="space-y-1 text-[11px]">
          <div className="opacity-50">
            <Formula latex={`R_{\\text{overall}} = \\max(w_D \\!\\times\\! R_D,\\; w_{FH} \\!\\times\\! R_{FH},\\; w_{CS} \\!\\times\\! R_{CS})`} />
          </div>
          <div>
            <Formula latex={`R_{\\text{overall}} = \\max(${riskMaxInner}) = \\mathbf{${calc.overall_risk}}`} />
          </div>
          <div className="opacity-50 mt-1">
            <Formula latex={`U_{\\text{overall}} = \\max(w_D \\!\\times\\! U_D,\\; w_{FH} \\!\\times\\! U_{FH},\\; w_{CS} \\!\\times\\! U_{CS})`} />
          </div>
          <div>
            <Formula latex={`U_{\\text{overall}} = \\max(${urgMaxInner}) = \\mathbf{${calc.overall_urgency}}`} />
          </div>
        </div>
      </div>

      {/* Step 3: Vulnerability modifiers */}
      <div className="border-t border-background/20" />
      <div>
        <div className="text-[10px] font-medium opacity-60 uppercase tracking-wider mb-1">
          {hasVuln ? 'Step 3 — Vulnerability modifiers' : 'Step 3 — Vulnerability modifiers (none detected)'}
        </div>
        {hasVuln ? (
          <>
            <div className="text-[10px] opacity-40 mb-1.5">
              Additive modifiers from detected vulnerability labels.
              {dims.vulnerability?.top_label && (
                <> Driven by: {formatTopLabel(dims.vulnerability.top_label)}.</>
              )}
            </div>
            <div className="space-y-1 text-[11px]">
              <div className="opacity-50">
                <Formula latex={`R_{\\text{total}} = R_{\\text{overall}} + V_R`} />
              </div>
              <div>
                <Formula latex={`R_{\\text{total}} = ${calc.overall_risk} + ${vRiskMod} = \\mathbf{${calc.total_risk}}`} />
              </div>
              <div className="opacity-50 mt-1">
                <Formula latex={`U_{\\text{total}} = U_{\\text{overall}} + V_U`} />
              </div>
              <div>
                <Formula latex={`U_{\\text{total}} = ${calc.overall_urgency} + ${vUrgMod} = \\mathbf{${calc.total_urgency}}`} />
              </div>
            </div>
          </>
        ) : (
          <div className="text-[10px] opacity-40">
            No vulnerability labels detected. R and U pass through unchanged.
          </div>
        )}
      </div>

      <div className="border-t border-background/20" />

      {/* Step 4: Final score */}
      <div>
        <div className="text-[10px] font-medium opacity-60 uppercase tracking-wider mb-1">
          Step 4 — Final score
        </div>
        <div className="space-y-1 text-[11px]">
          <div className="opacity-50">
            <Formula latex={`\\text{Score} = \\dfrac{w_R \\times R_{\\text{total}} + w_U \\times U_{\\text{total}}}{N} \\times 10`} />
          </div>
          <div>
            <Formula latex={`\\text{Score} = \\dfrac{1.9 \\times ${calc.total_risk} + 1.0 \\times ${calc.total_urgency}}{${normalizer}} \\times 10 = \\mathbf{${score.toFixed(2)}}`} />
          </div>
        </div>
      </div>

      {/* Consumer Duty footnote */}
      {dims.consumer_duty && ((dims.consumer_duty.risk ?? 0) > 0 || (dims.consumer_duty.urgency ?? 0) > 0) && (
        <>
          <div className="border-t border-background/20" />
          <div className="text-[10px] opacity-50 italic">
            Consumer Duty (R:{dims.consumer_duty.risk ?? 0} U:{dims.consumer_duty.urgency ?? 0}) — informational, not included in score
          </div>
        </>
      )}
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  starting: 'Starting...',
  analyzing: 'Analyzing...',
  queued: 'Queued',
  pending: 'Queued',
  classifying: 'Classifying...',
  summarising: 'Summarising...',
  summarizing: 'Summarising...',
  evaluating_ombudsman: 'Evaluating FOS...',
  'evaluating ombudsman': 'Evaluating FOS...',
};

function formatStageLabel(stage?: string | null): string {
  if (!stage) return 'Processing...';
  const label = STAGE_LABELS[stage.toLowerCase()];
  if (label) return label;
  return stage.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') + '...';
}

export function ConductScoreCircle({
  score,
  size = 'md',
  className,
  showDecimal = false,
  breakdown,
  isProcessing = false,
  processingStage,
}: ConductScoreCircleProps): React.ReactElement {
  const backgroundColor = getConductScoreColor(score);
  const config = sizeConfig[size];

  // Display value - rounded or with decimal
  const displayValue = showDecimal
    ? score.toFixed(1)
    : Math.max(0, Math.min(10, Math.round(score)));

  const circle = (
    <div className="relative inline-flex">
      <div
        className={cn(
          config.container,
          'rounded-full flex items-center justify-center shadow-sm',
          className
        )}
        style={{ backgroundColor }}
      >
        <span className={cn(config.text, 'font-semibold text-white leading-none')}>
          {displayValue}
        </span>
      </div>
      {isProcessing && (
        <div
          className="absolute inset-[-3px] rounded-full animate-processing-pulse border-2"
          style={{ borderColor: backgroundColor }}
        />
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {circle}
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {isProcessing ? (
          <span>{formatStageLabel(processingStage)}</span>
        ) : breakdown ? (
          <BreakdownTooltipContent score={score} breakdown={breakdown} />
        ) : (
          <span>Conduct Score: {score.toFixed(1)}/10</span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
